import React from "react";
import { motion } from "framer-motion";
import {
  FileText,
  Layers,
  Boxes,
  Link as LinkIcon,
  Anchor,
  Wrench,
  ArrowRight,
} from "lucide-react";

const items = [
  {
    icon: FileText,
    title: "Shop Drawings",
    body: "Fabrication-ready drawings for beams, columns, braces and assemblies with full bill of materials.",
  },
  {
    icon: Layers,
    title: "Erection Drawings",
    body: "Field-ready erection plans with clear member marks, elevations, and installation sequencing.",
  },
  {
    icon: Boxes,
    title: "3D Modeling & BIM",
    body: "Coordinated Tekla and Revit models for clash-free coordination with GC and MEP teams.",
  },
  {
    icon: LinkIcon,
    title: "Connection Design",
    body: "Engineered connections with calculations, backed by certified structural PEs.",
  },
  {
    icon: Anchor,
    title: "Anchor Bolt Plans",
    body: "Precise anchor bolt setting plans and templates to keep foundation work on schedule.",
  },
  {
    icon: Wrench,
    title: "Miscellaneous Steel",
    body: "Stairs, handrails, ladders, platforms and misc. metals detailed with fabrication in mind.",
  },
];

const scrollToContact = () => {
  const el = document.getElementById("contact");
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -72 });
  else el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export const Capabilities = () => (
  <section
    id="capabilities"
    className="relative py-20 md:py-28 border-t border-slate-200/60 bg-white"
  >
    <div className="px-6 md:px-10 lg:px-14 xl:px-16">
      {/* Header */}
      <div className="mb-4">
        <div className="inline-flex items-center gap-2 mb-6 text-xs font-mono uppercase tracking-[0.22em] text-slate-500">
          <span className="w-8 h-px bg-[#0A2540]" />
          04 · Capabilities
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 mb-14 lg:mb-20">
        <h2 className="lg:col-span-7 font-display text-3xl md:text-4xl lg:text-5xl font-semibold text-[#0A2540] tracking-tight leading-[1.1]">
          End-to-end steel detailing{" "}
          built around the{" "}
          <span className="text-slate-400">fabricator</span>.
        </h2>
        <div className="lg:col-span-5 flex lg:items-end">
          <p className="text-base md:text-lg text-slate-600 leading-relaxed max-w-xl">
            From shop drawings to full BIM coordination, we deliver detailing
            that keeps fabrication moving and erection crews on schedule. Every
            drawing set is checked, cross-verified and ready to build.
          </p>
        </div>
      </div>

      {/* Grid of 6 cards */}
      <div className="border-t border-slate-200 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
        {items.map((it, i) => {
          const Icon = it.icon;
          const num = String(i + 1).padStart(2, "0");
          const isRightCol = (i + 1) % 3 === 0;
          const isBottomRow = i >= 3;
          return (
            <motion.button
              key={it.title}
              type="button"
              onClick={scrollToContact}
              data-testid={`capability-card-${i}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.45, delay: (i % 3) * 0.06 }}
              className={`group relative text-left p-8 lg:p-10 flex flex-col min-h-[280px] border-slate-200 hover:bg-slate-50/60 transition-colors border-b ${
                isRightCol ? "" : "md:border-r"
              } ${isBottomRow ? "md:border-b-0" : ""} ${
                i % 3 === 2 ? "lg:border-r-0" : "lg:border-r"
              }`}
            >
              {/* Icon + number row */}
              <div className="flex items-start justify-between mb-8">
                <div className="w-12 h-12 rounded-md bg-[#0A2540] flex items-center justify-center group-hover:bg-[#0A2540] transition-colors">
                  <Icon className="w-5 h-5 text-white" strokeWidth={2} />
                </div>
                <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-slate-400">
                  /{num}
                </div>
              </div>

              <h3 className="font-display font-bold text-2xl text-[#0A2540] leading-tight">
                {it.title}
              </h3>
              <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                {it.body}
              </p>

              <div className="mt-auto pt-8 inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.22em] text-[#0A2540] group-hover:text-[#0A2540] transition-colors">
                Request scope
                <ArrowRight
                  className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1"
                  strokeWidth={2.5}
                />
              </div>
            </motion.button>
          );
        })}
      </div>
    </div>
  </section>
);
